import { FC } from 'react';
import { TextField, Typography } from '@mui/material';
import { ReactComponent as QuestionSVG } from 'assets/icons/question.svg';
import { getPayerFullName, getServiceInputConfig } from 'utils/helpers';
import {
	IBillCreateRequestArrayDataItem,
	IBillsSubBillItem,
	IGetServiceDataResponse,
	IPaymentPayerFormData,
	IProviderServiceResponse,
	IReceiptOtherServiceResponse,
} from 'utils/types';
import { ServiceFieldItem } from '../ServiceFieldItem';
import styles from './index.module.scss';

interface ProviderServiceDetailsProps {
	data:
		| IReceiptOtherServiceResponse
		| IProviderServiceResponse
		| IBillsSubBillItem
		| IGetServiceDataResponse
		| IBillCreateRequestArrayDataItem;
	providerAccountId?: string;
	amount?: string;
	payerData?: IPaymentPayerFormData | null;
	paymentDate?: string;
	instructionNumber?: string | number;
	title?: string;
	showBankName?: boolean;
}

export const ProviderServiceDetails: FC<ProviderServiceDetailsProps> = ({
	data,
	providerAccountId,
	amount,
	payerData,
	paymentDate,
	instructionNumber,
	title,
	showBankName = false,
}) => {
	const regDataObject =
		(data as IReceiptOtherServiceResponse)?.regDataObject ?? (data as IBillsSubBillItem)?.service?.regDataObject;

	if (!regDataObject) return null;

	const ultimateDebtor = payerData?.isPayerSameAsUltimateDebtor ? payerData : payerData?.ultimateDebtor;
	const paymentDetails = (
		<>
			{paymentDate && <TextField size="small" label="Дата" value={paymentDate} InputProps={{ readOnly: true }} />}
			{(data as IReceiptOtherServiceResponse).serviceFieldsValues?.length ? (
				(data as IReceiptOtherServiceResponse).serviceFieldsValues.map((item) => (
					<ServiceFieldItem
						{...getServiceInputConfig(item, (data as IReceiptOtherServiceResponse).serviceFields)}
						value={item.value}
						key={item.name}
						data={item}
						fieldConfig={(data as IReceiptOtherServiceResponse).serviceFields?.find(
							(fieldConfig) => item.name === fieldConfig.name
						)}
						size="small"
						InputProps={{ readOnly: true }}
					/>
				))
			) : (
				<>
					{!!amount && <TextField size="small" label="Сума" value={amount} InputProps={{ readOnly: true }} />}
					{!!(providerAccountId || (data as IBillsSubBillItem)?.service?.providerAccountId) && (
						<TextField
							size="small"
							label="Номер договору"
							value={providerAccountId || (data as IBillsSubBillItem)?.service?.providerAccountId}
							InputProps={{ readOnly: true }}
						/>
					)}
				</>
			)}
			<TextField size="small" label="ПІБ (повністю)" value={regDataObject.name} InputProps={{ readOnly: true }} />
			<TextField
				size="small"
				label="Розрахунковий рахунок"
				value={regDataObject.account}
				InputProps={{ readOnly: true }}
			/>
			{showBankName && regDataObject.bankName && (
				<TextField
					size="small"
					label="Банк отримувача"
					value={regDataObject.bankName}
					InputProps={{ readOnly: true }}
				/>
			)}
			<TextField
				size="small"
				label="ЄДРПОУ отримувача"
				value={regDataObject.zkpo}
				InputProps={{ readOnly: true }}
			/>
			<TextField
				className={styles.fullWidth}
				size="small"
				label="Призначення"
				value={regDataObject.description}
				InputProps={{ readOnly: true }}
			/>
		</>
	);
	const payerDetails = payerData && (
		<>
			<div className={styles.payerGroup}>
				<Typography className={styles.payerGroup__title} variant="subtitle1">
					Платник
				</Typography>
				<div className={styles.payerFields}>
					<TextField
						size="small"
						label="ПІБ"
						value={getPayerFullName(payerData)}
						InputProps={{ readOnly: true }}
					/>
					<TextField size="small" label="ІПН (РНОКПП)" value={payerData.taxId} InputProps={{ readOnly: true }} />
				</div>
			</div>
			<div className={styles.payerGroup}>
				<Typography className={styles.payerGroup__title} variant="subtitle1">
					Фактичний платник
				</Typography>
				<div className={styles.payerFields}>
					<TextField
						size="small"
						label="ПІБ"
						value={ultimateDebtor ? getPayerFullName(ultimateDebtor) : ''}
						InputProps={{ readOnly: true }}
					/>
					<TextField
						size="small"
						label="ІПН (РНОКПП)"
						value={ultimateDebtor?.taxId ?? ''}
						InputProps={{ readOnly: true }}
					/>
				</div>
			</div>
		</>
	);

	if (title) {
		return (
			<div className={styles.grouped}>
				<div className={styles.confirmation}>
					<QuestionSVG className={styles.confirmation__icon} />
					<Typography variant="subtitle1">
						Платіжна інструкція{instructionNumber !== undefined ? ` ${instructionNumber}` : ''}
					</Typography>
				</div>
				<section className={styles.card}>
					<Typography className={styles.card__title} variant="h5">
						{title}
					</Typography>
					<div className={styles.list}>{paymentDetails}</div>
				</section>
				{payerDetails && (
					<section className={styles.card}>
						<Typography className={styles.card__title} variant="h5">
							Введені дані
						</Typography>
						<div className={styles.payerList}>{payerDetails}</div>
					</section>
				)}
			</div>
		);
	}

	return <div className={styles.list}>{paymentDetails}</div>;
};
